import { Brand, DeckPrint, DeckViewer, Frame, Img } from "../headliner/DeckShell";
import { CinematicCover, CINE_ASSETS } from "../headliner/CinematicCover";
import { LogoStamp, Wordmark } from "../headliner/brands";
import { Deliver } from "../headliner/HeadlinerDeck";
import { Proposal } from "../headliner/Proposal";
import { WhatItIs } from "../headliner/WhatItIs";
import { Headliner } from "../buildpartner/BuildCover";
import { brandRootStyle, whyFor } from "./brands";
import "../buildpartner/build.css";
import "../lawpartner/law.css";
import "./frontier.css";

/* Frontier Partnership — fjerde deck i familien (samme ark, skall og PDF-pipeline). Papir, mottakerens farge som aksent.
   Tese: laben eier utviklerne; summiten gir den kjøperne. Master + mottakerlag: ?for=OpenAI|Microsoft|Google|Anthropic|…
   Alt scenerelatert: rettigheten er bekreftet, klokkeslett er foreslått og settes med programmet. Norge-fakta verifisert sep. 2026. */

/* 01 Cover — felles kinematisk cover (CinematicCover): Grieghallen full-bleed, Kevin (proposed) i høyre tredjedel, co-brand-lockup */
const Cover = ({ mottaker }) => (
  <CinematicCover
    lockup={<p className="bd-lockup" data-testid="fr-lockup"><Brand size={19} /><i>×</i><Wordmark name={mottaker || "Frontier Partner"} h={24} /></p>}
    label="Frontier Partnership"
    title={["Frontier models.", "Norwegian boards.", <span className="acc">One room.</span>]}
    sub={<>700 of Norway's leaders decide what their organisations will deploy. The frontier is set in the lab; adoption is decided here{mottaker ? <>, with <b>{mottaker}</b> at the centre.</> : "."}</>}
    index={[
      ["Date", "13 April 2027"],
      ["Venue", "Grieghallen, Bergen"],
      [mottaker ? "Prepared for" : "Audience", mottaker || "700 seats · invitation-only", mottaker ? "prep" : "", "hd-prepared"],
    ]}
  />
);

/* 04 The room — «700» + punktfeltet: rommet løser seg ut av støy (laben) til rader; ti punkter i mottakerens farge = partnerens seter */
const FrField = ({ mottaker }) => {
  const cols = 28, rows = 25, gap = 20, dots = [];
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
    const n = j * cols + i;
    const h = Math.imul(n + 1, 2654435761) >>> 0;
    const keep = j === 0 && i >= 9 && i <= 18;
    dots.push(
      <circle
        key={n} cx={i * gap + 3} cy={j * gap + 3} r="2.6" className={keep ? "keep" : undefined}
        style={{ "--dx": `${((h % 200) - 100) * 1.7}px`, "--dy": `${(((h >>> 8) % 200) - 100) * 1.7}px`, animationDelay: `${0.25 + (((h >>> 16) % 100) / 100) * 1.1}s` }}
      />,
    );
  }
  return (
    <figure className="fr-field" data-testid="fr-field" aria-hidden>
      <svg viewBox={`0 0 ${(cols - 1) * gap + 6} ${(rows - 1) * gap + 6}`} width={(cols - 1) * gap + 6} height={(rows - 1) * gap + 6} fill="currentColor">{dots}</svg>
      <figcaption className="fr-field-cap" data-testid="fr-field-cap"><b>700</b> seats. One room.<i className="acc">10 seats</i>for {mottaker || "the Frontier Partner"}.</figcaption>
    </figure>
  );
};

const FrRoom = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The room" mottaker={mottaker} className="hd-room lw-room fr-room">
    <div className="hd-big" data-testid="fr-room-700">700</div>
    <p className="hd-sub lw-room-sub">Invite-only. Curated by the summit.<br />Every seat a deployment decision.</p>
    <FrField mottaker={mottaker} />
  </Frame>
);

/* 02 The question — skiftet ER sliden: tre bevegelser fra venstre (2023, grå) til høyre (2027, blekk), én linje hver */
const SHIFT = [
  ["Developers", "Decision-makers"],
  ["Demos", "Deployment"],
  ["Capability", "Trust"],
];

const Question = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The question" mottaker={mottaker} className="fr-q">
    <h2 className="hd-h2 bd-h80" data-testid="fr-q-h">The frontier has moved.</h2>
    <div className="fr-shift" data-testid="fr-shift">
      <div className="hd"><span className="a">2023 · where the labs won</span><span className="b">2027 · where it is decided</span></div>
      {SHIFT.map(([a, b], i) => (
        <div className="row" key={a} style={{ "--i": i }}>
          <span className="from">{a}</span>
          <span className="track" aria-hidden><i className="o" /><i className="f" /></span>
          <span className="to">{b}</span>
        </div>
      ))}
    </div>
    <p className="bd-foot fr-q-foot" data-testid="fr-q-p">The labs have won the builders. What decides 2027 is the room where boards choose what to deploy, on which platform, under which rules — and on 13 April, 700 of them sit in it at once. This deck puts one lab at the centre of that room.</p>
  </Frame>
);

/* 06 Why Norway — verifiserte fakta, ingen superlativer */
const NORWAY = [
  ["Compute", "Stargate Norway — OpenAI's first European site under OpenAI for Countries — is being built outside Narvik by Nscale and Aker: 230 MW initially, 100,000 NVIDIA GPUs targeted by the end of 2026."],
  ["Cloud", "Microsoft has operated two Norwegian cloud regions since 2019. Google broke ground on a €600 million data centre in Skien in 2024."],
  ["Power", "Hydropower, a cool climate and an electricity mix that is almost entirely renewable — the reason the frontier's infrastructure is arriving here."],
  ["Market", "A high-trust, high-adoption economy: a sovereign wealth fund, a large public sector, energy, finance, maritime and industry — the sectors in the room."],
];

export const WhyNorway = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Why Norway" mottaker={mottaker} className="lw-law fr-norway">
    <h2 className="hd-h2 bd-h80 lw-law-h" data-testid="fr-norway-h">Norway is already<br />on your map.</h2>
    <ul className="hd-ledger lw-ledger" data-testid="fr-norway-points">
      {NORWAY.map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="bd-foot">The infrastructure is arriving. The decisions about what runs on it are made by the people in this room.</p>
  </Frame>
);

/* 07 Why <lab> */
const WhyYou = ({ nr, mottaker }) => (
  <Frame nr={nr} label={`Why ${mottaker}`} mottaker={mottaker} className="hd-whyyou">
    <h2 className="hd-whyyou-h" data-testid="fr-whyyou-h">Why {mottaker}.</h2>
    <ul className="hd-whyyou-ledger" data-testid="fr-whyyou-points">
      {whyFor(mottaker).map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <LogoStamp name={mottaker} sub="Frontier Partner · Norwegian AI Summit 2027" />
    <p className="hd-whyyou-foot" data-testid="fr-whyyou-foot">That makes {mottaker} the natural Frontier Partner.</p>
  </Frame>
);

/* 08 The position — tre eierskap */
const POSITION = [
  ["The position", ["Official Frontier Partner of Norwegian AI Summit 2027", "Category exclusivity — the only frontier lab or hyperscaler in the partner programme", "Presence across the summit's relevant surfaces", "Other labs may appear as speakers — none as partners"]],
  ["The conversation", ["The Frontier Keynote — 25 minutes on the main stage, a confirmed element of the partnership", "The Adoption Room — a closed session with 15–25 CEOs and board members, partner hosted", "The Norway Index — a co-created survey of the 700, published with the partner as co-author"]],
  ["The access", ["10 seats for the lab and its customers", "A table of 10 at the Summit Dinner", "The Frontier Floor in the AI Experience", "Curated introductions through the day", "Recording rights to your own segment"]],
];

const Position = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The position" mottaker={mottaker} className="lw-pos">
    <h2 className="hd-h2 bd-h80" data-testid="fr-pos-h">One lab anchors<br />the frontier conversation.</h2>
    <div className="lw-cols" data-testid="fr-pos-cols">
      {POSITION.map(([t, items]) => (
        <div className="col" key={t}>
          <span className="t">{t}</span>
          <ul>{items.map((x) => <li key={x}>{x}</li>)}</ul>
        </div>
      ))}
    </div>
    <p className="hd-one-univ" data-testid="fr-pos-universe">
      {UNIVERS.map((u, i) => (
        <span key={u} className={u === "Frontier" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
      ))}
    </p>
  </Frame>
);

/* 09 The Frontier Keynote — garantert scenetid; klokkeslett foreslått */
const KEYNOTE = [
  ["11:30", "Proposed slot", "After the opening session, before lunch — the whole room in the same seat. The final slot is set with the programme."],
  ["25 min", "The next twelve months", "The view from the frontier, for the people who decide what to do with it — delivered by the lab's research or product lead."],
  ["1 voice", "The partner on the main stage", "Introduced as the Frontier Partner. Editorial framing, set together with the summit."],
  ["Yours", "Recorded and cleared", "The partner may use the recording of its own segment in its own channels."],
];

const Keynote = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Frontier Keynote" mottaker={mottaker} className="lw-rules">
    <h2 className="hd-h2 bd-h80" data-testid="fr-keynote-h">25 minutes. Main stage.<br />The frontier, explained by {mottaker || "the partner"}.</h2>
    <div className="lw-rules-grid" data-testid="fr-keynote-points">
      {KEYNOTE.map(([t, l, d]) => (
        <div className="hd-block" key={l}>
          <span className="t">{t}</span>
          <span className="l">{l}</span>
          <span className="d">{d}</span>
        </div>
      ))}
    </div>
    <p className="bd-foot">A confirmed element of the partnership. Substance is what makes it credible — thought leadership, not a product launch.</p>
  </Frame>
);

/* 10 The Adoption Room */
const ADOPT = [
  ["Invitation only", "15–25 CEOs and board members — 8 seats for the partner's own customers, the rest curated by the summit"],
  ["45 minutes", "A conversation, not a demo"],
  ["Partner hosted", "The lab's enterprise lead opens the room and frames the questions. Working theme: from pilots to production — set together"],
  ["Designed into the day", "Placed in the programme during the AI Experience. Proposed 13:30, set with the programme"],
];

const Adoption = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Adoption Room" mottaker={mottaker} className="hd-exec lw-brief">
    <h2 className="hd-h2 big" data-testid="fr-adopt-h">15–25 CEOs.<br />One closed room.</h2>
    <p className="hd-exec-sub">A private conversation on what it takes to deploy frontier AI at scale in 2027 — hosted by {mottaker || "the Frontier Partner"}, with a curated group of CEOs, board members and owners from the summit's audience.</p>
    <div className="hd-exec-grid" data-testid="fr-adopt-points">
      {ADOPT.map(([t, d]) => (
        <div className="hd-point" key={t}><span className="t">{t}</span><span className="d">{d}</span></div>
      ))}
    </div>
    <p className="hd-exec-foot">The most senior buyers of the day — and the lab's name on the door.</p>
  </Frame>
);

/* 11 The Norway Index — data-eiendelen */
const INDEX = (m) => [
  ["The survey", "A pre-summit survey of the 700 on AI adoption, priorities and barriers — designed together, run by the summit."],
  ["The launch", "Headline findings presented in the Frontier Keynote — the first time the room sees itself in numbers."],
  ["The report", `Published after the summit with ${m || "the partner"} as co-author — distributed to all 700 and to the summit's wider network.`],
  ["The asset", "Norwegian board-level adoption data no one else has — for the partner's own conversations through 2027."],
];

const Index = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The Norway Index" mottaker={mottaker} className="lw-take">
    <h2 className="hd-h2 bd-h80" data-testid="fr-index-h">The Norway Index.<br />What 700 leaders will deploy in 2027.</h2>
    <ul className="hd-ledger lw-ledger" data-testid="fr-index-points">
      {INDEX(mottaker).map(([k, v]) => (
        <li key={k}><span className="k">{k}</span><span className="v">{v}</span></li>
      ))}
    </ul>
    <p className="bd-foot">Labs have benchmarks for models. Nobody has one for Norwegian boards.</p>
  </Frame>
);

/* 12 Beyond the stage */
const BEYOND = [
  ["The Frontier Floor", "The lab's presence in the AI Experience — demonstrations with a purpose, not square metres.", "/images/vision-expo-v3.webp"],
  ["The network", "CEOs · board members · CFOs · founders · investors — curated introductions through the day.", "/images/vision-networking-v3.webp"],
  ["Summit Dinner", "The conversation continues after the stage closes.", "/images/vision-dinner-v4.webp"],
];

const Beyond = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Beyond the stage" mottaker={mottaker} className="hd-beyond">
    <h2 className="hd-h2" data-testid="fr-beyond-h">Presence with a function.</h2>
    <div className="hd-frags" data-testid="fr-beyond-frags">
      {BEYOND.map(([n, d, img]) => (
        <figure className="hd-frag" key={n}>
          <div className="ph"><Img src={img} alt={n} draggable={false} /></div>
          <figcaption><span className="n">{n}</span><span className="d">{d}</span></figcaption>
        </figure>
      ))}
    </div>
  </Frame>
);

/* 13 The day */
const DAG = [
  ["09:00", "Arrival"], ["09:30", "Opening keynote · Kevin O'Leary (proposed)"], ["11:30", "The Frontier Keynote", true], ["13:00", "AI Experience · Frontier Floor", true],
  ["13:30", "The Adoption Room", true], ["16:00", "Closing keynote"], ["18:30", "Dinner · Spissen"],
];

const Day = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The day" mottaker={mottaker} className="hd-day">
    <h2 className="hd-h2" data-testid="fr-day-h">Three moments in a designed day.</h2>
    <ol className="hd-timeline" data-testid="fr-timeline">
      {DAG.map(([t, l, hi]) => (
        <li key={t} className={hi ? "hi" : ""}><span className="t">{t}</span><i className="pt" /><span className="l">{l}</span></li>
      ))}
    </ol>
    <p className="hd-dayfoot">Highlighted: the Frontier Partner's moments. The Frontier Keynote and the Adoption Room are confirmed elements of the partnership; times shown are proposed and set with the programme. The Frontier Floor is designed with the partner.</p>
  </Frame>
);

/* 14 The economics — struktur uten tall */
const INCLUDED = [
  "The Frontier Partner position, category exclusive", "The Frontier Keynote — 25 minutes, main stage", "The Adoption Room — hosted, 15–25 seats",
  "The Norway Index — co-authored, to all 700", "10 seats · a table of 10 at the Summit Dinner", "The Frontier Floor in the AI Experience",
  "Content, broadcast and recording rights", "The partner report",
];
const OPTIONAL = ["The model on stage — the intelligence behind the Build Lab, subject to agreement with the Build Partner", "Ecosystem — up to three implementation partners on the Frontier Floor", "A hosted customer dinner the evening before"];

const Economics = ({ nr, mottaker }) => (
  <Frame nr={nr} label="The economics" mottaker={mottaker} className="lw-econ">
    <h2 className="hd-h2 bd-h80" data-testid="fr-econ-h">One fee.<br />Everything in the position.</h2>
    <div className="lw-econ-grid" data-testid="fr-econ">
      <div className="col">
        <span className="t">Included</span>
        <ul>{INCLUDED.map((x) => <li key={x}>{x}</li>)}</ul>
      </div>
      <div className="col">
        <span className="t">Optional</span>
        <ul>{OPTIONAL.map((x) => <li key={x}>{x}</li>)}</ul>
        <p className="lvl" data-testid="fr-econ-level">Indicative level shared in conversation. One partner — decided by the summit, not auctioned.</p>
      </div>
    </div>
  </Frame>
);

/* 16 One lab */
const SCARCE = ["One Frontier Partner", "No other lab or hyperscaler in the partner programme", "Frontier speaking time on the main stage reserved for the partner", "Decided by the summit — not sold twice"];
const UNIVERS = ["Founding", "Headliner", "Frontier", "Build", "AI Law & Policy", "Dinner", "Broadcast"];

export const OneLab = ({ nr, mottaker }) => (
  <Frame nr={nr} label="One lab" mottaker={mottaker} className="hd-one">
    <h2 className="hd-one-h" data-testid="fr-one-h">One category.<br />One Frontier Partner.</h2>
    <p className="hd-one-sub">One lab owns the frontier conversation at Norwegian AI Summit — with category exclusivity in the partner programme.</p>
    <p className="hd-one-line" data-testid="fr-one-line">One lab · one category · no competing position.</p>
    <ul className="hd-one-pts" data-testid="fr-one-points">{SCARCE.map((k) => <li key={k}>{k}</li>)}</ul>
    <p className="hd-one-univ" data-testid="fr-one-universe">
      {UNIVERS.map((u, i) => (
        <span key={u} className={u === "Frontier" ? "on" : ""}>{u}{i < UNIVERS.length - 1 && <i> · </i>}</span>
      ))}
    </p>
  </Frame>
);

/* 17 On one page */
const ONEPAGE = (m) => [
  ["What", `${m || "One frontier lab"} as the Frontier Partner of Norwegian AI Summit 2027 — category exclusive.`],
  ["Why", "One room, built for 700 of Norway's leaders, decides what their organisations will deploy. The frontier is set in the lab — adoption is decided here."],
  ["You get", "The Frontier Keynote (25 min, main stage) · The Adoption Room (15–25 CEOs) · The Norway Index, co-authored · 10 seats and a dinner table · the Frontier Floor · recording rights · partner report."],
  ["Investment", "One fee, category exclusive. Indicative level shared in conversation."],
  ["Timeline", "Position confirmed by 1 October 2026 · designed together Q4 2026 · announced with the programme."],
];

const OnePage = ({ nr, mottaker }) => (
  <Frame nr={nr} label="On one page" mottaker={mottaker} className="lw-onepage">
    <h2 className="hd-h2 bd-h80" data-testid="fr-onepage-h">On one page.</h2>
    <dl className="lw-onepage-grid" data-testid="fr-onepage">
      {ONEPAGE(mottaker).map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  </Frame>
);

/* 18 Next step */
const STEPS = [
  ["01", "Conversation", "A 45-minute meeting on the position and the lab's ambitions in Norway."],
  ["02", "Term sheet", "One page: position, deliverables, fee, exclusivity — and data, content and recording rights."],
  ["03", "Design together", "The Frontier Keynote, the Adoption Room and the Norway Index — Q4 2026."],
  ["04", "Announcement", "Announced with the programme, as the Frontier Partner."],
];

const Next = ({ nr, mottaker }) => (
  <Frame nr={nr} label="Next step" mottaker={mottaker} className="hd-next lw-next">
    <h2 className="hd-next-h" data-testid="fr-next-h">One Frontier Partnership.</h2>
    <p className="hd-next-p" data-testid="fr-next-p">
      13 April 2027. Grieghallen, Bergen. Built for 700 of Norway's decision-makers.<br />
      {mottaker ? `We would like to explore ${mottaker} as the Frontier Partner.` : "We are selecting one frontier lab for the Frontier position."}
    </p>
    <ol className="lw-steps" data-testid="fr-steps">
      {STEPS.map(([n, t, d]) => (
        <li key={n}><span className="n">{n}</span><span className="t">{t}</span><span className="d">{d}</span></li>
      ))}
    </ol>
    <p className="hd-next-terms lw-deadline" data-testid="fr-deadline"><span className="k">Decision</span>Position confirmed by <b>1 October 2026</b> · designed together Q4 2026 · announced with the programme.</p>
    <div className="hd-contact" data-testid="hd-contact">
      <span className="n">Martin · Founder, Norwegian AI Summit</span>
      <span className="e">martin@norwegianaisummit.no</span>
    </div>
  </Frame>
);

const FrHeadliner = (p) => <Headliner {...p} lead="Proposed opening keynote. The international headliner sets the ambition level of the room — an investor who has moved from talking about AI to developing the infrastructure behind it." />;
const FrDeliver = (p) => <Deliver {...p} label="Built to deliver" />;
const FrProposal = ({ nr, mottaker }) => (
  <Proposal
    nr={nr} mottaker={mottaker}
    sentence={<><b>{mottaker}</b> as the Frontier Partner of Norwegian AI Summit 2027 — the lab the room decides with.</>}
    index={[["The Frontier Keynote", "25 minutes · main stage"], ["The Adoption Room", `15–25 CEOs · hosted by ${mottaker}`], ["Position", "One lab. Category exclusive."]]}
  />
);

/* «One lab» kuttet (eksklusiviteten sies på Position/Economics/One page; universlinjen på Position) */
const SLIDES = [Cover, WhatItIs, Question, FrRoom, FrHeadliner, WhyNorway, Position, Keynote, Adoption, Index, Beyond, Day, Economics, FrDeliver, OnePage, Next];
/* Personalisert: «Why <lab>» etter Why Norway, «The proposal» som signatur til slutt */
const slidesFor = (mottaker) => (mottaker ? [...SLIDES.slice(0, 6), WhyYou, ...SLIDES.slice(6), FrProposal] : SLIDES);

const ASSETS = ["/images/headliner/kevin-src-hi.webp", ...CINE_ASSETS, "/images/vision-hall-keynote.webp", "/images/vision-expo-v3.webp", "/images/vision-networking-v3.webp", "/images/vision-dinner-v4.webp"];

export function FrontierPrint() {
  return <DeckPrint slides={slidesFor} fallback="" theme="paper" venue="none" rootStyle={brandRootStyle} />;
}

export default function FrontierDeck({ mottaker = "", publicMode = false }) {
  return <DeckViewer deck="frontier" slides={slidesFor} fallback={mottaker} filePrefix="Norwegian-AI-Summit-Frontier-Partnership" assets={ASSETS} theme="paper" venue="none" categoryField={false} publicMode={publicMode} rootStyle={brandRootStyle} />;
}
